import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDesign } from '../../designs';

const MAPS_KEY = (process.env.REACT_APP_GOOGLE_MAPS_KEY || '').trim();

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

interface PlaceDetails {
  name: string;
  address: string;
  lat?: number;
  lng?: number;
  url?: string;
}

interface PlacesAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (details: PlaceDetails) => void;
  citiesOnly?: boolean;
}

// Load the Google Maps JS API once
let mapsLoaded = false;
let mapsLoadPromise: Promise<void> | null = null;

export function loadMapsApi(): Promise<void> {
  if (mapsLoaded) return Promise.resolve();
  if (mapsLoadPromise) return mapsLoadPromise;
  if (!MAPS_KEY) return Promise.reject(new Error('No Maps API key'));

  mapsLoadPromise = new Promise((resolve, reject) => {
    const w = window as any;
    if (w.google?.maps?.places) {
      mapsLoaded = true;
      resolve();
      return;
    }

    const callbackName = '_kreweGmapsInit';
    w[callbackName] = () => {
      mapsLoaded = true;
      delete w[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete w[callbackName];
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });
  return mapsLoadPromise;
}

/**
 * Fetches autocomplete suggestions using the new Places API
 * (AutocompleteSuggestion — required for projects created after March 2025).
 */
async function fetchSuggestions(input: string, citiesOnly: boolean): Promise<Suggestion[]> {
  const google = (window as any).google;
  if (!google?.maps?.places?.AutocompleteSuggestion) return [];

  try {
    const request: any = { input };
    if (citiesOnly) {
      request.includedPrimaryTypes = ['(cities)'];
    }

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    return (suggestions || [])
      .filter((s: any) => s.placePrediction)
      .slice(0, 5)
      .map((s: any) => ({
        placeId: s.placePrediction.placeId,
        mainText: s.placePrediction.mainText?.text || '',
        secondaryText: s.placePrediction.secondaryText?.text || '',
        fullText: s.placePrediction.text?.text || '',
      }));
  } catch (err) {
    console.error('[Maps] AutocompleteSuggestion error:', err);
    return [];
  }
}

/**
 * Fetches place details using the new Place class
 * (required for projects created after March 2025).
 */
async function fetchPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
  const google = (window as any).google;
  if (!google?.maps?.places?.Place) return null;

  try {
    const place = new google.maps.places.Place({ id: placeId });
    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'googleMapsURI'] });

    return {
      name: place.displayName || '',
      address: place.formattedAddress || '',
      lat: place.location?.lat(),
      lng: place.location?.lng(),
      url: place.googleMapsURI || undefined,
    };
  } catch (err) {
    console.error('[Maps] Place.fetchFields error:', err);
    return null;
  }
}

export default function PlacesAutocomplete({
  label,
  value,
  onChange,
  onPlaceSelect,
  citiesOnly = false,
}: PlacesAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!MAPS_KEY) return;
    loadMapsApi().then(() => {
      const w = window as any;
      if (w.google?.maps?.places) {
        setApiReady(true);
      }
    }).catch(() => {});
  }, []);

  // Close dropdown on outside click/touch
  useEffect(() => {
    const handler = (e: Event) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const handleFetch = useCallback(async (input: string) => {
    const results = await fetchSuggestions(input, citiesOnly);
    setSuggestions(results);
    setShowDropdown(results.length > 0);
  }, [citiesOnly]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (apiReady && val.length >= 2) {
      debounceRef.current = setTimeout(() => handleFetch(val), 250);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = async (suggestion: Suggestion) => {
    // Always use mainText (venue name only) — not the full address string
    onChange(suggestion.mainText);
    setShowDropdown(false);
    setSuggestions([]);

    if (onPlaceSelect) {
      const details = await fetchPlaceDetails(suggestion.placeId);
      if (details) {
        onPlaceSelect({
          name: details.name || suggestion.mainText,
          address: details.address || suggestion.fullText,
          lat: details.lat,
          lng: details.lng,
          url: details.url,
        });
      }
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;

  return (
    <div ref={containerRef} style={{ marginBottom: '16px', position: 'relative' }}>
      <label
        style={{
          display: 'block',
          fontFamily: "'Raleway', sans-serif",
          fontSize: fs.sectionHeader,
          fontWeight: 700,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: d.textSecondary,
          marginBottom: '6px',
        }}
      >
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={(e) => { suggestions.length > 0 && setShowDropdown(true); e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
        onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
        autoComplete="off"
        style={{
          width: '100%',
          padding: '10px 12px',
          fontFamily: "'Raleway', sans-serif",
          fontSize: fs.bodyInput,
          fontWeight: 400,
          color: d.textPrimary,
          backgroundColor: d.bgSurface,
          border: '1px solid rgba(200,146,58,0.10)',
          borderRadius: '2px',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      {showDropdown && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: d.bgElevated,
          border: '1px solid rgba(200,146,58,0.15)',
          borderRadius: '2px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 200,
          maxHeight: '200px',
          overflowY: 'auto',
        }}>
          {suggestions.map((s) => (
            <div
              key={s.placeId}
              onClick={() => handleSelect(s)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid rgba(200,146,58,0.08)',
              }}
            >
              <span style={{
                fontFamily: "'Raleway', sans-serif",
                fontSize: fs.body,
                color: d.textPrimary,
                display: 'block',
              }}>
                {s.mainText}
              </span>
              <span style={{
                fontFamily: "'Raleway', sans-serif",
                fontSize: fs.detail,
                color: d.textSecondary,
              }}>
                {s.secondaryText}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
